/*
 * Pure string URL auto-linking utilities.
 *
 * Safe for any context including backend — no React dependency.
 */

const URL_WITH_PREFIX_REGEX = /(\S)?(https?:\/\/[^\s<>)"',]+)/g;

/** Strips common trailing punctuation that is unlikely to be part of a URL. */
const trimTrailingPunct = (url: string): string =>
  url.replace(/[.,;:!?*_~`]+$/, '');

type Segment = { chunk: string; preserve: boolean };

/** Emits a plain (linkifiable) segment if there is accumulated text since `plainStart`. */
function flushPlain(
  result: Segment[],
  text: string,
  plainStart: number,
  pos: number,
) {
  if (pos > plainStart) {
    result.push({ chunk: text.slice(plainStart, pos), preserve: false });
  }
}

/** Matches a fenced code block starting at `pos` (```...```). Returns end pos or -1. */
function matchFencedCode(text: string, pos: number): number {
  if (
    text[pos] !== '`' ||
    text[pos + 1] !== '`' ||
    text[pos + 2] !== '`'
  ) return -1;
  const end = text.indexOf('```', pos + 3);
  return end !== -1 ? end + 3 : text.length;
}

/** Matches an inline code span starting at `pos` (`...`). Returns end pos or -1. */
function matchInlineCode(text: string, pos: number): number {
  if (text[pos] !== '`') return -1;
  const end = text.indexOf('`', pos + 1);
  return end !== -1 ? end + 1 : -1;
}

/** Matches a markdown autolink <https://...> starting at `pos`. Returns end pos or -1. */
function matchAutolink(text: string, pos: number): number {
  if (text[pos] !== '<') return -1;
  if (!text.startsWith('https://', pos + 1) && !text.startsWith('http://', pos + 1)) return -1;
  const end = text.indexOf('>', pos + 1);
  return end !== -1 && end - pos <= 2048 ? end + 1 : -1;
}

/** Matches a markdown link [text](url) starting at `pos`. Returns end pos or -1. */
function matchMarkdownLink(text: string, pos: number): number {
  if (text[pos] !== '[') return -1;
  const closeBracket = text.indexOf(']', pos + 1);
  if (closeBracket === -1 || closeBracket - pos > 501) return -1;
  if (text[closeBracket + 1] !== '(') return -1;
  const closeParen = text.indexOf(')', closeBracket + 2);
  if (closeParen === -1 || closeParen - closeBracket > 2002) return -1;
  return closeParen + 1;
}

/**
 * Splits `text` into segments that should be preserved (code blocks, inline
 * code, existing markdown links/autolinks) and segments that are plain text
 * eligible for URL linkification.
 *
 * Uses an iterative character scanner instead of a single regex to avoid the
 * polynomial backtracking (ReDoS) that CodeQL flags for patterns like
 * `\[[^\]]*\]\([^)]*\)`.
 */
function splitPreserved(text: string): Segment[] {
  const result: Segment[] = [];
  let pos = 0;
  let plainStart = 0;
  const len = text.length;

  while (pos < len) {
    const fencedEnd = matchFencedCode(text, pos);
    const inlineEnd = matchInlineCode(text, pos);
    const autolinkEnd = matchAutolink(text, pos);
    const mdLinkEnd = matchMarkdownLink(text, pos);

    const firstMatch = [fencedEnd, inlineEnd, autolinkEnd, mdLinkEnd].find(e => e !== -1) ?? -1;

    if (firstMatch !== -1) {
      flushPlain(result, text, plainStart, pos);
      result.push({ chunk: text.slice(pos, firstMatch), preserve: true });
      pos = plainStart = firstMatch;
    } else {
      pos++;
    }
  }

  flushPlain(result, text, plainStart, len);

  return result;
}

export const autoLinkUrls = (text: string): string => {
  return splitPreserved(text)
    .map(seg => {
      if (seg.preserve) return seg.chunk;
      return seg.chunk.replace(URL_WITH_PREFIX_REGEX, (_, prefix, url) => {
        const cleanUrl = trimTrailingPunct(url);
        const trailing = url.slice(cleanUrl.length);
        if (prefix) return `${prefix} [${cleanUrl}](${cleanUrl})${trailing}`;
        return `[${cleanUrl}](${cleanUrl})${trailing}`;
      });
    })
    .join('');
};
